import React from 'react'
import { Prompt, Redirect, withRouter } from 'react-router-dom'
import moment from 'moment'
import Header from '../containers/HeaderContainer'
import LogoEditorModal from './LogoEditorModal'
import AssetLibraryModal from './AssetLibraryModal'
import PublicPreviewModal from './PublicPreviewModal'
import { blankClient, cloneClient } from '../helpers/clientDefaults'

const SKIN_OPTIONS = [
    {
        key: 'classic',
        label: 'Classic',
        description: 'The current raffle presentation with bold chevrons and the familiar Split 50-50 layout.'
    },
    {
        key: 'spotlight',
        label: 'Full Width',
        description: 'A bold mobile-first treatment with full-width prize and payout bars, keeping the original colors while stretching key moments edge to edge.'
    }
]

const ExternalLinkIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="h-4 w-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5" />
        <path strokeLinecap="round" strokeLinejoin="round" d="m19 5-8 8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 14.5v2.75A1.75 1.75 0 0 1 17.25 19h-10.5A1.75 1.75 0 0 1 5 17.25V6.75A1.75 1.75 0 0 1 6.75 5H9.5" />
    </svg>
)

const BackIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 6 9 12l6 6" />
    </svg>
)

class ClientDetail extends React.Component {
    logoInputRef = React.createRef()
    suppressUnsavedPrompt = false

    state = {
        busy: false,
        savingClient: false,
        checkingUser: true,
        creating: false,
        draft: blankClient(),
        initialSnapshot: JSON.stringify(blankClient()),
        bundleRows: [],
        notice: '',
        activeTab: 'client',
        logoEditorOpen: false,
        logoEditorImage: '',
        backgroundLibraryOpen: false,
        previewOpen: false
    }

    componentDidMount() {
        window.addEventListener('beforeunload', this._handleBeforeUnload)
        const creating = this._clientId() === 'new'
        if (!this.props.user) {
            this.setState({ checkingUser: true, creating, activeTab: this._requestedTab() }, () => {
                this.props.checkUserSession(() => {
                    this.setState({ checkingUser: false })
                    this._loadPage()
                })
            })
        } else {
            this.setState({ checkingUser: false, creating, activeTab: this._requestedTab() }, () => this._loadPage())
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.setState({
                creating: this._clientId() === 'new',
                draft: blankClient(),
                bundleRows: this._bundleRowsFromObject(blankClient().bundles),
                notice: '',
                activeTab: this._requestedTab()
            }, () => this._loadPage())
        } else if (prevProps.location.search !== this.props.location.search) {
            this.setState({ activeTab: this._requestedTab() })
        }
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this._handleBeforeUnload)
    }

    _clientId() {
        const parts = this.props.location.pathname.split('/')
        return parts[parts.length - 1]
    }

    _requestedTab() {
        const search = this.props.location && this.props.location.search ? this.props.location.search : ''
        return search.indexOf('tab=events') > -1 ? 'events' : 'client'
    }

    _bundleRowsFromObject(bundles) {
        return Object.keys(bundles || {})
            .sort((a, b) => Number(a) - Number(b))
            .map((price) => ({
                price: String(price),
                count: bundles[price] === undefined || bundles[price] === null ? '' : String(bundles[price])
            }))
    }

    _bundleObjectFromRows(rows) {
        const bundles = {}
        ;(rows || []).forEach((row) => {
            const price = String(row.price || '').trim()
            const count = row.count
            if (price === '' || count === '' || count === null || count === undefined) return
            bundles[price] = count
        })
        return bundles
    }

    _normalizedClientDraft(draft = this.state.draft, bundleRows = this.state.bundleRows) {
        const normalized = cloneClient(draft || blankClient())
        normalized.bundles = this._bundleObjectFromRows(bundleRows)
        if (normalized.styles && !normalized.styles.hero) delete normalized.styles.hero
        return JSON.stringify(normalized)
    }

    _isDirty() {
        if (this.suppressUnsavedPrompt) return false
        return this._normalizedClientDraft() !== this.state.initialSnapshot
    }

    _handleBeforeUnload = (event) => {
        if (!this._isDirty()) return
        event.preventDefault()
        event.returnValue = ''
    }

    _loadPage() {
        const clientId = this._clientId()
        if (clientId === 'new') {
            const nextDraft = blankClient()
            this.setState({
                draft: nextDraft,
                bundleRows: this._bundleRowsFromObject(nextDraft.bundles),
                notice: '',
                busy: false,
                activeTab: this._requestedTab(),
                initialSnapshot: this._normalizedClientDraft(nextDraft, this._bundleRowsFromObject(nextDraft.bundles))
            }, () => {
                this.suppressUnsavedPrompt = false
            })
            return
        }

        this.setState({ busy: true, notice: '' }, () => {
            this.props.fetchClients(() => {
                const client = (this.props.clients || []).find((item) => item.PK === clientId)
                if (!client) {
                    const nextDraft = blankClient()
                    this.setState({ busy: false, draft: nextDraft, bundleRows: this._bundleRowsFromObject(nextDraft.bundles), notice: 'Client not found.' }, () => {
                        this.suppressUnsavedPrompt = false
                    })
                    return
                }
                const nextDraft = cloneClient(client)
                this.setState({
                    draft: nextDraft,
                    bundleRows: this._bundleRowsFromObject(nextDraft.bundles),
                    initialSnapshot: this._normalizedClientDraft(nextDraft, this._bundleRowsFromObject(nextDraft.bundles))
                })
                this.props.fetchEvents(clientId, () => {
                    this.setState({ busy: false }, () => {
                        this.suppressUnsavedPrompt = false
                    })
                })
            })
        })
    }

    _updateField(field, value) {
        const draft = { ...this.state.draft, [field]: value }
        if (field === 'stub') {
            draft.stub = String(value).toLowerCase().replace(/[^a-z0-9-]/g, '')
            draft.promoMessage = draft.promoMessage || ('Buy tickets at s50.events/' + draft.stub)
        }
        this.setState({ draft })
    }

    _updateBundleRow(index, field, value) {
        const bundleRows = this.state.bundleRows.map((row, rowIndex) => (
            rowIndex === index ? { ...row, [field]: value } : row
        ))
        const draft = cloneClient(this.state.draft)
        draft.bundles = this._bundleObjectFromRows(bundleRows)
        this.setState({ draft, bundleRows })
    }

    _addBundleRow() {
        const bundleRows = [...this.state.bundleRows, { price: '', count: '' }]
        this.setState({ bundleRows })
    }

    _removeBundleRow(index) {
        const bundleRows = this.state.bundleRows.filter((_, rowIndex) => rowIndex !== index)
        const draft = cloneClient(this.state.draft)
        draft.bundles = this._bundleObjectFromRows(bundleRows)
        this.setState({ draft, bundleRows })
    }

    _updateStyle(key, value) {
        const draft = cloneClient(this.state.draft)
        draft.styles[key] = value
        this.setState({ draft })
    }

    _secondaryBorderColor(value) {
        const stringValue = String(value || '').trim()
        if (!stringValue) return '#000000'
        if (stringValue.indexOf('solid') > -1) {
            const match = stringValue.match(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\))/)
            return match ? match[1] : '#000000'
        }
        return stringValue
    }

    _copyToClipboard(value, notice) {
        const text = String(value || '')
        if (!text) return

        const onSuccess = () => this.setState({ notice: notice || 'Copied to clipboard.' })

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(onSuccess).catch(() => {
                const input = document.createElement('input')
                input.value = text
                document.body.appendChild(input)
                input.select()
                document.execCommand('copy')
                document.body.removeChild(input)
                onSuccess()
            })
            return
        }

        const input = document.createElement('input')
        input.value = text
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        onSuccess()
    }

    _assetType(key) {
        return key === 'logo' ? 'image' : (key === 'background' ? 'background' : 'color')
    }

    _isColorKey(key) {
        return [
            'primaryBtnBg',
            'primaryBtnTxt',
            'secondaryBtnBorder',
            'secondaryBtnTxt',
            'splitPrizeBg',
            'splitPrizeTxt',
            'livePrizePoolLabelBg',
            'livePrizePoolLabelTxt',
            'ticketsSoldTxt',
            'bodyTxt'
        ].indexOf(key) > -1
    }

    _assetPreviewStyle(value) {
        if (!value) return {}
        if (value.indexOf('gradient(') > -1 || value.indexOf('url(') > -1) {
            return {
                backgroundImage: value,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }
        }
        return {
            backgroundImage: 'url(' + value + ')',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
        }
    }

    _saveClient() {
        const draft = cloneClient(this.state.draft)
        draft.bundles = this._bundleObjectFromRows(this.state.bundleRows)
        if (draft.styles && !draft.styles.hero) delete draft.styles.hero
        if (!draft.name || !draft.stub || !draft.charity) {
            this.setState({ notice: 'Name, stub, and charity are required.' })
            return
        }
        if (!draft.promoMessage) {
            draft.promoMessage = 'Buy tickets at s50.events/' + draft.stub
        }

        const action = this.state.creating ? this.props.createClient : this.props.updateClient
        const payload = this.state.creating ? { client: draft, createInitialEvent: true } : { clientId: draft.PK, client: draft }

        this.setState({ savingClient: true, notice: '' }, () => {
            action(payload, (response) => {
                if (response && response.error) {
                    const message = response.message || 'Unable to save client.'
                    const shouldCheckSession = message === 'Unable to update client.' || message === 'Unable to create client.'

                    if (!shouldCheckSession) {
                        this.suppressUnsavedPrompt = false
                        this.setState({ savingClient: false, notice: message })
                        return
                    }

                    this.props.checkUserSession((user) => {
                        if (!user) {
                            this.suppressUnsavedPrompt = true
                            this.setState({ savingClient: false, notice: '' }, () => {
                                this.props.history.replace('/')
                            })
                            return
                        }

                        this.suppressUnsavedPrompt = false
                        this.setState({ savingClient: false, notice: message })
                    })
                    return
                }

                const savedClient = response && response.client ? response.client : draft
                this.suppressUnsavedPrompt = true
                this.setState({
                    savingClient: false,
                    creating: false,
                    notice: 'Client saved successfully.'
                }, () => {
                    const nextId = savedClient.PK || this._clientId()
                    this.props.history.replace('/clients/' + nextId)
                    this._loadPage()
                })
            })
        })
    }

    _generateQr() {
        const client = this.state.draft
        const base64 = btoa(JSON.stringify(client))
        const path = 'https://admin.s50.events/promo/?client=' + base64
        this.props.generatePDF(path, (res) => {
            if (res && res.url) window.open(res.url, '_blank')
        })
    }

    _createNextEvent() {
        if (!this.state.draft.PK) return
        if (!window.confirm('Create the next event for this client?')) return
        this.setState({ busy: true, notice: '' }, () => {
            this.props.createNextEvent({ clientId: this.state.draft.PK }, (response) => {
                if (response && response.error) {
                    this.setState({ busy: false, notice: response.message || 'Unable to create event.' })
                    return
                }
                this.setState({ busy: false, notice: 'New event created.' }, () => this._loadPage())
            })
        })
    }

    _renderTextField(label, field, options = {}) {
        const draft = this.state.draft
        const value = draft[field] || ''
        const baseClass = 'admin-input mt-2 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-400'
        const type = options.type || 'text'

        return (
            <label className={options.className || ''}>
                <div className="text-sm font-semibold text-slate-900 dark:text-white">{label}</div>
                {options.textarea ?
                    <textarea className={baseClass} rows={options.rows || 3} value={value} onChange={(e) => this._updateField(field, e.target.value)} placeholder={options.placeholder || ''} />
                    :
                    <input className={baseClass} type={type} inputMode={options.inputMode} value={value} onChange={(e) => this._updateField(field, e.target.value)} placeholder={options.placeholder || ''} />
                }
                {options.hint ? <div className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">{options.hint}</div> : null}
            </label>
        )
    }

    _logoFrameClass(frame) {
        if (frame === 'circle') return 'overflow-hidden rounded-full'
        if (frame === 'rounded') return 'overflow-hidden rounded-[28px]'
        if (frame === 'square') return 'overflow-hidden rounded-none'
        return ''
    }

    _logoImageStyle(styles = {}) {
        return {
            transform: 'translate(' + ((Number(styles.logoOffsetX || 0) * 100).toFixed(3)) + '%, ' + ((Number(styles.logoOffsetY || 0) * 100).toFixed(3)) + '%) scale(' + Number(styles.logoScale || 1) + ')',
            transformOrigin: 'center center'
        }
    }

    _openLogoPicker() {
        if (this.logoInputRef.current) this.logoInputRef.current.click()
    }

    _openLogoEditorFromExisting() {
        const currentLogo = this.state.draft.styles.logo || ''
        this.setState({
            logoEditorOpen: true,
            logoEditorImage: currentLogo
        })
    }

    _handleLogoFile(event) {
        const file = event.target.files && event.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = () => {
            this.setState({
                logoEditorOpen: true,
                logoEditorImage: reader.result
            })
        }
        reader.readAsDataURL(file)
        event.target.value = ''
    }

    _applyLogoEdit(result) {
        const nextLogo = result && result.dataUrl ? result.dataUrl : ''
        if (!nextLogo) return

        const finishApply = (logoUrl) => {
            const draft = cloneClient(this.state.draft)
            draft.styles.logo = logoUrl
            draft.styles.logoBg = result.logoBg || ''
            draft.styles.logoFrame = result.logoFrame || 'none'
            draft.styles.logoScale = Number(result.logoScale || 1)
            draft.styles.logoOffsetX = Number(result.logoOffsetX || 0)
            draft.styles.logoOffsetY = Number(result.logoOffsetY || 0)
            this.setState({
                busy: false,
                draft,
                logoEditorOpen: false,
                logoEditorImage: '',
                notice: 'Logo updated in the draft. Save client when you are happy with it.'
            })
        }

        if (nextLogo.indexOf('data:') !== 0) {
            finishApply(nextLogo)
            return
        }

        const fileNameBase = (this.state.draft.stub || this.state.draft.name || 'client-logo')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || 'client-logo'

        this.setState({ busy: true, notice: 'Uploading logo asset...' }, () => {
            this.props.uploadAsset({
                assetType: 'logo',
                fileName: fileNameBase + '.png',
                dataUrl: nextLogo
            }, (response) => {
                if (!response || response.error || !response.asset || !response.asset.url) {
                    this.setState({
                        busy: false,
                        notice: (response && response.message) || 'Unable to upload the logo asset.'
                    })
                    return
                }
                finishApply(response.asset.url)
            })
        })
    }

    _selectBackgroundAsset(asset) {
        if (!asset || !asset.url) return
        const draft = cloneClient(this.state.draft)
        draft.styles.background = 'url("' + asset.url + '")'
        this.setState({
            draft,
            backgroundLibraryOpen: false,
            notice: 'Background updated from the shared asset library.'
        })
    }

    _renderBackgroundPanel() {
        const value = this.state.draft.styles.background || ''
        const overlayColor = this.state.draft.styles.backgroundOverlayColor || '#000000'
        const overlayOpacity = this.state.draft.styles.backgroundOverlayOpacity === undefined || this.state.draft.styles.backgroundOverlayOpacity === null ? 0 : this.state.draft.styles.backgroundOverlayOpacity

        return (
            <div className="rounded-3xl border border-slate-200 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/5">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Background</div>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Background</span>
                </div>
                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white/80 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
                    Managed through the asset library and uploader workflow.
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                    <button type="button" className="inline-flex items-center justify-center rounded-xl bg-[#1cd997] px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:brightness-95" onClick={() => this.setState({ backgroundLibraryOpen: true })}>
                        Choose from library
                    </button>
                    <button type="button" className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={() => this.setState({ backgroundLibraryOpen: true })}>
                        Upload new background
                    </button>
                </div>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white/80 p-4 dark:border-slate-800 dark:bg-slate-950/70">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Background overlay</div>
                    <div className="mt-3 grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                        <label>
                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Colour</div>
                            <input className="mt-2 h-11 w-full cursor-pointer rounded-xl border border-slate-300 bg-white p-1 dark:border-slate-700 dark:bg-slate-950" type="color" value={overlayColor} onChange={(e) => this._updateStyle('backgroundOverlayColor', e.target.value)} />
                        </label>
                        <label>
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Opacity</div>
                                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">{Math.round(Number(overlayOpacity || 0) * 100)}%</div>
                            </div>
                            <input className="mt-4 w-full accent-sky-500" type="range" min="0" max="1" step="0.01" value={overlayOpacity} onChange={(e) => this._updateStyle('backgroundOverlayOpacity', Number(e.target.value))} />
                        </label>
                    </div>
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
                    {value ?
                        <div className="relative h-[520px] w-full bg-cover bg-center bg-no-repeat" style={this._assetPreviewStyle(value)}>
                            <div className="absolute inset-0 pointer-events-none" style={{ background: overlayColor, opacity: Number(overlayOpacity || 0) }} />
                        </div>
                        :
                        <div className="flex h-[520px] items-center justify-center px-4 text-sm text-slate-400 dark:text-slate-500">
                            No background selected yet.
                        </div>}
                </div>
            </div>
        )
    }

    _renderSkinSelector() {
        const selectedSkin = this.state.draft.styles.skin || 'classic'
        const selectedOption = SKIN_OPTIONS.find((option) => option.key === selectedSkin) || SKIN_OPTIONS[0]

        return (
            <section className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Public skin</h2>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Choose the mobile-first look and feel used on the public landing and Buy Tickets pages.</p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                        {selectedOption.label}
                    </span>
                </div>
                <div className="mt-6 grid gap-4 lg:grid-cols-2">
                    {SKIN_OPTIONS.map((option) => (
                        <button
                            key={option.key}
                            type="button"
                            onClick={() => this._updateStyle('skin', option.key)}
                            className={'rounded-[24px] border px-5 py-5 text-left transition ' + (selectedSkin === option.key
                                ? 'border-[#1cd997] bg-[#1cd997]/10 ring-2 ring-[#1cd997]/35 shadow-[0_20px_45px_-28px_rgba(28,217,151,0.9)]'
                                : 'border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-slate-700 dark:hover:bg-slate-950')}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <span className="text-base font-semibold text-slate-900 dark:text-white">{option.label}</span>
                                    {selectedSkin === option.key ?
                                        <span className="ml-2 inline-flex rounded-full bg-[#1cd997]/15 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0f766e] dark:text-[#1cd997]">
                                            Selected
                                        </span>
                                        : null}
                                </div>
                                <span className={'inline-flex h-9 w-9 items-center justify-center rounded-full border text-xs font-bold ' + (selectedSkin === option.key
                                    ? 'border-[#1cd997] bg-white text-[#0f766e] dark:bg-slate-950 dark:text-[#1cd997]'
                                    : 'border-slate-300 text-slate-400 dark:border-slate-700 dark:text-slate-500')}>
                                    {option.key === 'classic' ? 'C' : 'F'}
                                </span>
                            </div>
                            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{option.description}</p>
                        </button>
                    ))}
                </div>
            </section>
        )
    }

    _renderLogoPanel() {
        const value = this.state.draft.styles.logo || ''
        const logoBg = this.state.draft.styles.logoBg || ''
        const logoFrame = this.state.draft.styles.logoFrame || 'none'

        return (
            <div className="rounded-3xl border border-slate-200 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/5">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Logo</div>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Image</span>
                </div>
                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white/80 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
                    Upload a file, crop it client-side, optionally remove its background, and choose how it should be presented on the public page.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                        Frame: {logoFrame}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                        Background: {logoBg ? logoBg : 'none'}
                    </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                    <input ref={this.logoInputRef} type="file" accept="image/*" className="hidden" onChange={(event) => this._handleLogoFile(event)} />
                    <button type="button" className="inline-flex items-center justify-center rounded-xl bg-[#1cd997] px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:brightness-95" onClick={() => this._openLogoPicker()}>
                        {value ? 'Replace logo' : 'Upload logo'}
                    </button>
                    {value ?
                        <button type="button" className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={() => this._openLogoEditorFromExisting()}>
                            Edit logo
                        </button>
                        : null}
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
                    {value ?
                        <div className="flex h-[360px] w-full items-center justify-center p-8">
                            <div
                                className={'s50-preview__logo-frame relative h-[240px] w-[240px] ' + this._logoFrameClass(logoFrame)}
                                style={logoBg ? { background: logoBg } : null}
                            >
                                <img src={value} alt="logo" className="s50-preview__logo" style={this._logoImageStyle(this.state.draft.styles)} />
                            </div>
                        </div>
                        :
                        <div className="flex h-[360px] items-center justify-center px-4 text-sm text-slate-400 dark:text-slate-500">
                            No logo selected yet.
                        </div>}
                </div>
            </div>
        )
    }

    _renderAssetCard(key, options = {}) {
        const draft = this.state.draft
        const assetType = this._assetType(key)
        const value = draft.styles[key] || ''
        const containerClass = options.containerClassName || ''
        const previewClass = options.previewClassName || ''
        const emptyClass = options.emptyClassName || ''

        return (
            <div key={key} className={'rounded-3xl border border-slate-200 bg-slate-50/90 p-4 dark:border-white/10 dark:bg-white/5 ' + containerClass}>
                <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold capitalize text-slate-900 dark:text-white">{key}</div>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">{assetType}</span>
                </div>

                {this._isColorKey(key) ?
                    <div className="mt-3 flex items-center gap-3">
                        <input className="h-10 w-12 rounded-lg border border-slate-200 bg-transparent p-1 dark:border-white/10" type="color" value={key === 'secondaryBtnBorder' ? this._secondaryBorderColor(value) : (value || '#000000')} onChange={(e) => this._updateStyle(key, e.target.value)} />
                        <input
                            className="admin-input block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-500 focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 dark:border-white/10 dark:bg-slate-900 dark:text-white"
                            value={key === 'secondaryBtnBorder' ? this._secondaryBorderColor(value) : (value || '')}
                            onChange={(e) => this._updateStyle(key, e.target.value)}
                            placeholder="#000000"
                        />
                        <button
                            type="button"
                            onClick={() => this._copyToClipboard(key === 'secondaryBtnBorder' ? this._secondaryBorderColor(value) : value, key + ' copied to clipboard.')}
                            className="inline-flex h-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 px-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                            title={'Copy ' + key}
                            aria-label={'Copy ' + key}
                        >
                            Copy
                        </button>
                    </div>
                    :
                    <div className="mt-3">
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/80 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
                            Managed through the asset library and uploader workflow.
                        </div>
                    </div>}

                {(assetType === 'image' || assetType === 'background') ?
                    <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10">
                        {value ?
                            assetType === 'background' ?
                                <div className={'w-full ' + previewClass} style={this._assetPreviewStyle(value)} />
                                :
                                <img src={value} alt={key} className={previewClass} style={{ width: '100%', height: 128, objectFit: 'contain', display: 'block', padding: 16 }} />
                            :
                            <div className={'flex items-center justify-center px-4 text-sm text-slate-400 dark:text-slate-500 ' + (assetType === 'background' ? 'w-full' : 'h-32') + ' ' + emptyClass}>
                                No asset selected yet.
                            </div>}
                    </div>
                    : null}
            </div>
        )
    }

    _renderEventsTab(events, liveEvent) {
        const draft = this.state.draft

        return (
            <section className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Event management</h2>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Live events are highlighted first, and the rest stay in a simple list you can jump into.</p>
                    </div>
                </div>

                {!this.state.creating && draft.PK && !liveEvent ?
                    <button className="mt-5 inline-flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-semibold text-slate-950 transition hover:brightness-95" style={{ background: '#1cd997' }} onClick={() => this._createNextEvent()}>
                        Create next event
                    </button>
                    : null}

                {this.state.creating ? <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">The initial event will be created after you save this client.</div> : null}
                {!this.state.creating && events.length === 0 ? <div className="mt-5 text-sm text-slate-500 dark:text-slate-400">No events loaded for this client.</div> : null}

                {!this.state.creating && events.length > 0 ?
                    <div className="mt-5 overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800">
                        {events.map((event, index) => (
                            <div key={event.SK} className={'border-b border-slate-200 px-4 py-2.5 last:border-b-0 dark:border-slate-800 ' + (index % 2 === 1 ? 'bg-slate-50/95 dark:bg-slate-900/55' : 'bg-white/95 dark:bg-slate-950/70')}>
                                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <div className="text-sm font-semibold text-slate-900 dark:text-white">{event.SK}</div>
                                            <span className={'inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ' + (event.completed
                                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300'
                                                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300')}>
                                                {event.completed ? 'Completed' : 'Live'}
                                            </span>
                                        </div>
                                        <div className="mt-2 grid grid-cols-2 gap-x-5 gap-y-1 md:grid-cols-4 xl:grid-cols-4">
                                            <div>
                                                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Prize pool</div>
                                                <div className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">${Number(event.prizePool || 0).toFixed(2)}</div>
                                            </div>
                                            <div>
                                                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Tickets sold</div>
                                                <div className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">{event.ticketCount || 0}</div>
                                            </div>
                                            <div>
                                                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Max prize</div>
                                                <div className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">${Number(event.maxPrizePool || 0).toFixed(2)}</div>
                                            </div>
                                            <div>
                                                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Draw date</div>
                                                <div className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">{event.drawDate ? moment(event.drawDate).format('DD MMM YYYY h:mma') : 'Not set'}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <a href={'/events/' + draft.PK + '/' + event.SK + '/edit'} className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white no-underline transition hover:bg-slate-800 dark:bg-sky-500 dark:text-slate-950 dark:hover:bg-sky-400">
                                            Manage event
                                        </a>
                                        <a href={'/events/' + draft.PK + '/' + event.SK} className="inline-flex items-center justify-center rounded-2xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900">
                                            Report
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    : null}
            </section>
        )
    }

    render() {
        if (this.state.checkingUser || this.state.busy) {
            return <div className="px-4 py-5 text-center">Loading client workspace...</div>
        }

        if (!this.props.user) {
            return <Redirect to='/' />
        }

        const draft = this.state.draft
        const colorKeys = [
            'primaryBtnBg',
            'primaryBtnTxt',
            'secondaryBtnBorder',
            'secondaryBtnTxt',
            'splitPrizeBg',
            'splitPrizeTxt',
            'livePrizePoolLabelBg',
            'livePrizePoolLabelTxt',
            'ticketsSoldTxt',
            'bodyTxt'
        ]
        const events = (this.props.events || []).slice().sort((a, b) => String(b.SK).localeCompare(String(a.SK)))
        const liveEvent = events.find((event) => !event.completed)
        const inputClass = 'admin-input mt-2 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-400'
        const canShowEventsTab = !this.state.creating && draft.PK
        const isDirty = this._isDirty()

        return (
            <div className="min-h-screen lg:flex">
                <Prompt
                    when={isDirty}
                    message="You have unsaved client changes. Leave this page without saving?"
                />
                <Header />
                <main className="min-w-0 flex-1 px-4 py-4 pb-32 sm:px-6 lg:px-10 lg:py-8 lg:pb-36">
                    <div className="mx-auto max-w-7xl">
                        <div className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                                <div className="max-w-3xl">
                                    <a href="/clients" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-sky-600 no-underline dark:text-sky-300">{BackIcon} Back to clients</a>
                                    <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
                                        {this.state.creating ? 'Create client' : (draft.name || 'Client details')}
                                    </h1>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    {!this.state.creating && draft.stub ?
                                        <a
                                            href={'https://s50.events/' + draft.stub}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                                        >
                                            Public page
                                            {ExternalLinkIcon}
                                        </a>
                                        : null}
                                    {!this.state.creating && draft.PK ?
                                        <button className="inline-flex items-center justify-center rounded-2xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={() => this._generateQr()}>
                                            Generate QR
                                        </button>
                                        : null}
                                </div>
                            </div>
                            {this.state.notice ? <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200">{this.state.notice}</div> : null}
                        </div>

                        <div className="admin-tab-strip mt-6 inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                            <button
                                onClick={() => this.setState({ activeTab: 'client' })}
                                className={'rounded-xl px-4 py-2 text-sm font-semibold transition ' + (this.state.activeTab === 'client'
                                    ? 'bg-slate-900 text-white dark:bg-sky-500 dark:text-slate-950'
                                    : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-900')}
                            >
                                Client
                            </button>
                            {canShowEventsTab ?
                                <button
                                    onClick={() => this.setState({ activeTab: 'events' })}
                                    className={'rounded-xl px-4 py-2 text-sm font-semibold transition ' + (this.state.activeTab === 'events'
                                        ? 'bg-slate-900 text-white dark:bg-sky-500 dark:text-slate-950'
                                        : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-900')}
                                >
                                    Events
                                </button>
                                : null}
                        </div>

                        {this.state.activeTab === 'client' || !canShowEventsTab ?
                            <div className="mt-6 space-y-6">
                                <section className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Client profile</h2>
                                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Core details used across the public raffle experience.</p>
                                        </div>
                                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                                            {draft.stub || 'new-client'}
                                        </span>
                                    </div>

                                    <div className="mt-6 grid gap-5 md:grid-cols-2">
                                        {this._renderTextField('Client name', 'name', { placeholder: 'Split 50-50 Club' })}
                                        {this._renderTextField('Stub', 'stub', { inputMode: 'url', placeholder: 'club-name', hint: 'Used in s50.events/<stub> URLs.' })}
                                        {this._renderTextField('Charity', 'charity', { placeholder: 'Foundation or club name' })}
                                        {this._renderTextField('Charity alt label', 'charityAlt', { placeholder: 'Optional alternate display label' })}
                                        {this._renderTextField('Contact email', 'email', { type: 'email', placeholder: 'ops@example.com' })}
                                        {this._renderTextField('Terms URL', 'terms', { type: 'url', placeholder: 'https://...' })}
                                        {this._renderTextField('Default max prize ($)', 'defaultMaxPrize', { type: 'number', placeholder: '50000' })}
                                        <label className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                                            <input type="checkbox" checked={!!draft.rolling} onChange={(e) => this._updateField('rolling', e.target.checked)} />
                                            Rolling client
                                        </label>
                                    </div>
                                </section>

                                <section className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Messaging</h2>
                                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Copy blocks the web client pulls into promo and checkout surfaces.</p>
                                    <div className="mt-6 grid gap-5 md:grid-cols-2">
                                        {this._renderTextField('Promo message', 'promoMessage', { textarea: true, rows: 4, placeholder: 'Buy tickets at s50.events/...' })}
                                        {this._renderTextField('Opt-in message', 'optInMessage', { textarea: true, rows: 4, placeholder: 'Subscriber consent copy' })}
                                        {this._renderTextField('Alt promo copy', 'altPromo', { textarea: true, rows: 4, className: 'md:col-span-2', placeholder: 'Optional alternate promo message' })}
                                    </div>
                                </section>

                                <section className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Ticket bundles</h2>
                                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Set the bundle mapping the raffle pages use at checkout.</p>
                                        </div>
                                    </div>
                                    <div className="mt-6 space-y-3">
                                        {this.state.bundleRows.map((row, index) => (
                                            <div key={'bundle-' + index} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/60">
                                                <div className="shrink-0 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Price ($)</div>
                                                <input className={inputClass + ' mt-0 flex-1'} type="number" min="0" step="0.01" value={row.price} onChange={(e) => this._updateBundleRow(index, 'price', e.target.value)} />
                                                <div className="shrink-0 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Ticket count</div>
                                                <input className={inputClass + ' mt-0 flex-1'} type="number" min="0" step="1" value={row.count} onChange={(e) => this._updateBundleRow(index, 'count', e.target.value)} />
                                                <div className="flex shrink-0 items-center justify-end">
                                                    <button type="button" className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={() => this._removeBundleRow(index)}>
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <button type="button" className="inline-flex items-center justify-center rounded-xl border border-dashed border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={() => this._addBundleRow()}>
                                            Add bundle
                                        </button>
                                    </div>
                                </section>

                                {this._renderSkinSelector()}

                                <section className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Branding and assets</h2>
                                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Image-style assets are previewed here and will move to uploader-only management.</p>
                                        </div>
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                                            URL editing is hidden for image assets so the library and uploader can become the single management path.
                                        </div>
                                    </div>

                                    <div className="mt-6 grid gap-5 md:grid-cols-2">
                                        <div>{this._renderBackgroundPanel()}</div>
                                        <div>{this._renderLogoPanel()}</div>
                                    </div>

                                    <div className="mt-5 grid gap-5 md:grid-cols-2">
                                        {colorKeys.map((key) => this._renderAssetCard(key))}
                                    </div>
                                </section>
                            </div>
                            :
                            <div className="mt-6">
                                {this._renderEventsTab(events, liveEvent)}
                            </div>}
                    </div>
                </main>
                <LogoEditorModal
                    open={this.state.logoEditorOpen}
                    image={this.state.logoEditorImage}
                    logoBg={this.state.draft.styles.logoBg || ''}
                    logoFrame={this.state.draft.styles.logoFrame || 'none'}
                    logoScale={this.state.draft.styles.logoScale || 1}
                    logoOffsetX={this.state.draft.styles.logoOffsetX || 0}
                    logoOffsetY={this.state.draft.styles.logoOffsetY || 0}
                    onClose={() => this.setState({ logoEditorOpen: false, logoEditorImage: '' })}
                    onApply={(result) => this._applyLogoEdit(result)}
                />
                <AssetLibraryModal
                    open={this.state.backgroundLibraryOpen}
                    assetType="background"
                    title="Background library"
                    description="Pick an existing shared background or upload a new one for every client to reuse."
                    onClose={() => this.setState({ backgroundLibraryOpen: false })}
                    onSelect={(asset) => this._selectBackgroundAsset(asset)}
                    onSelectUploaded={(asset) => this._selectBackgroundAsset(asset)}
                    loadAssets={(assetType, callback) => this.props.listAssets(assetType, callback)}
                    uploadAsset={(data, callback) => this.props.uploadAsset(data, callback)}
                />
                <PublicPreviewModal
                    open={this.state.previewOpen}
                    client={draft}
                    event={liveEvent}
                    onClose={() => this.setState({ previewOpen: false })}
                />
                <div className="fixed bottom-4 right-4 z-40">
                    <div className="flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/92 p-2 shadow-2xl backdrop-blur dark:border-slate-800 dark:bg-slate-950/92">
                        <button
                            type="button"
                            className={'inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition hover:bg-slate-50 dark:hover:bg-slate-900 ' + (isDirty
                                ? 'border-[#1cd997] text-[#0f766e] dark:text-[#1cd997]'
                                : 'border-slate-300 text-slate-700 dark:border-slate-700 dark:text-slate-200')}
                            onClick={() => this.setState({ previewOpen: true })}
                        >
                            {isDirty ?
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-2 w-2 rounded-full bg-[#1cd997] shadow-[0_0_0_3px_rgba(28,217,151,0.18)]"
                                />
                                : null}
                            Preview
                        </button>
                        <button
                            type="button"
                            disabled={!isDirty || this.state.savingClient}
                            className="inline-flex items-center justify-center rounded-full px-3 py-2 text-xs font-semibold text-slate-950 transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ background: '#1cd997' }}
                            onClick={() => this._saveClient()}
                        >
                            {this.state.savingClient ?
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-950/25 border-t-slate-950" />
                                    {this.state.creating ? 'Creating...' : 'Saving...'}
                                </span>
                                :
                                (this.state.creating ? 'Create client' : 'Save client')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }
}

export default withRouter(ClientDetail)
